<template>
  <div>
    <card v-if="status !== 'COMPUTING'" class="slds-m-bottom--small">
      <div class="container">
        <button class="slds-button slds-button_neutral" @click.once="computePreview" :disabled="isView">
          {{ $t('campaignDesigner.finalCheckAndCampaignRelease.computePreview') }}
        </button>
      </div>
    </card>
    <card v-if="['COMPUTING', 'COMPUTED', 'ERROR'].includes(status)" :has-empty-title="status === 'COMPUTED' && !!previewData && !!Object.keys(this.previewData).length">
      <spinner v-if="loading" />
      <template v-else>
        <div class="container" v-if="status === 'COMPUTING'">
          <span class="slds-text-heading_medium">
            {{ $t('campaignDesigner.finalCheckAndCampaignRelease.computingPreview') }}
          </span>
          <spinner class="spinner"/>
        </div>
        <div v-else-if="status === 'ERROR'">
          <section class="slds-modal_prompt slds-fade-in-open">
            <div class="slds-modal__container">
              <header class="slds-modal__header slds-theme_error slds-theme_alert-texture">
                <h2 class="slds-text-heading_medium" id="prompt-heading-id">{{$t('campaignDesigner.finalCheckAndCampaignRelease.previewError')}}</h2>
              </header>
              <div class="slds-modal__content slds-p-around_medium" id="prompt-message-wrapper">
                <p>{{previewError.message}}</p>
              </div>
            </div>
          </section>
        </div>
        <div v-else-if="status === 'COMPUTED'">
          <template v-if="previewData && Object.keys(previewData).length">
            <!-- Table -->
             <table class="slds-table slds-table_cell-buffer slds-table_bordered slds-m-bottom--medium">
                <thead>
                  <tr class="slds-line-height_reset">
                    <th class="" scope="col">
                      <div class="slds-truncate">{{$t('campaignDesigner.finalCheckAndCampaignRelease.numberOfContacts')}}</div>
                    </th>
                    <th class="" scope="col">
                      <div class="slds-truncate">{{$t('campaignDesigner.finalCheckAndCampaignRelease.numberOfCars')}}</div>
                    </th>
                    <th class="" scope="col">
                      <div class="slds-truncate">{{$t('campaignDesigner.finalCheckAndCampaignRelease.numberOfContactsEmailGranted')}}</div>
                    </th>
                    <th class="" scope="col">
                      <div class="slds-truncate">{{$t('campaignDesigner.finalCheckAndCampaignRelease.numberOfContactsSmsGranted')}}</div>
                    </th>
                    <th class="" scope="col">
                      <div class="slds-truncate">{{$t('campaignDesigner.finalCheckAndCampaignRelease.numberOfDistinctEmail')}}</div>
                    </th>
                    <th class="" scope="col">
                      <div class="slds-truncate">{{$t('campaignDesigner.finalCheckAndCampaignRelease.numberOfDistinctSMS')}}</div>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>
                      <div class="slds-truncate">{{previewData.totalCount.contact}}</div>
                    </td>
                    <td>
                      <div class="slds-truncate">{{previewData.totalCount.car}}</div>
                    </td>
                    <td>
                      <div class="slds-truncate">{{previewData.totalCount.contactEmailGranted}}</div>
                    </td>
                    <td>
                      <div class="slds-truncate">{{previewData.totalCount.contactSmsGranted}}</div>
                    </td>
                    <td>
                      <div class="slds-truncate">
                        {{previewData.totalCount.email}}
                      </div>
                    </td>
                    <td>
                      <div class="slds-truncate">
                        {{previewData.totalCount.sms}}
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            <!-- Charts -->
            <div class="slds-grid slds-wrap" v-if="previewData && previewData.totalCount.contact != 0">
              <card :title="chart.title" additional-classes="slds-col slds-size_1-of-2 slds-card_boundary" v-for="(chart, index) in charts" :key="`t_${index}`">
                <template #actions>
                  <button class="slds-button slds-button_icon slds-button_icon-brand" @click="download(previewData[chart.key], chart.title, chart.translate)">
                    <svg class="slds-button__icon" aria-hidden="true">
                      <use xlink:href="../../assets/symbols.svg#download"></use>
                    </svg>
                  </button>
                </template>
                <g-chart
                  :type="chart.type"
                  :data="chart.data"
                  :options="chart.options"
                  :resizeDebounce="500" />
              </card>
            </div>
            <div v-else>
              {{$t('campaignDesigner.finalCheckAndCampaignRelease.emptyPreview')}}
            </div>
          </template>
          <span class="slds-text-heading_medium" v-else>
            {{ $t('campaignDesigner.finalCheckAndCampaignRelease.emptyPreview') }}
          </span>
        </div>
      </template>
    </Card>
  </div>
</template>
<script>
import _ from 'lodash'
import { exportAsCsv } from '../utils/fileUtils'
import { getErrorMessage } from '../utils/utils'
import HasMode from '../utils/HasMode'
import Card from '../slds/Card'
import Spinner from '../../components/Spinner'
const TARGET_SOURCE = 'fast_counter'
export default {
  mixins: [HasMode],
  components: {
    Card,
    Spinner
  },
  props: {
    loading: Boolean,
    mode: String
  },
  computed: {
    status () {
      if (this.previewData && this.jobId && this.computedPreview && Object.keys(this.previewData).length >= 0) {
        return 'COMPUTED'
      }
      if (this.jobId && this.previewError) {
        return 'ERROR'
      }
      if (this.jobId) {
        return 'COMPUTING'
      }
      return 'MISSING'
    }
  },
  data () {
    return {
      previewId: '',
      channelsTable: [],
      charts: [],
      computedPreview: false,
      jobId: '',
      refreshJobInterval: null,
      previewData: {},
      previewError: undefined,
      showChannelsTable: false,
      targetSource: ''
    }
  },
  async activated () {
    this.$emit('startLoading')
    await this.callLoadFromStore()
    this.$emit('endLoading')
  },
  methods: {
   async callLoadFromStore () {
      const preview = this.$store.getters['fastCounter/currentPreview']
      this.targetSource = TARGET_SOURCE
      this.previewId = preview.id
      this.jobId = preview.config.preview && preview.config.preview.jobId ? preview.config.preview.jobId : null

      if (this.jobId) {
        await this.poll()
      }
    },
    async computePreview () {
      try {
        if (this.status === 'COMPUTED') {
          this.$store.dispatch('fastCounter/clearPreview')
          await this.callLoadFromStore()
          this.previewData = []
          this.jobId = null
          this.computedPreview = false
        }
        let functionToCall
        if (this.isCreate) {
          functionToCall = 'save'
        } else {
          functionToCall = 'update'
        }
        this.$emit('callFunction', { functionToCall, callback: this.startComputePreview })
      } catch (error) {
        this.showToast('error', this.$t('general.error'), 'text', getErrorMessage(error))
      }
    },
    download (data, fileName, translate) {
      const header = Object.keys(data).map(h => translate ? this.$t(`fastCounter.finalCheck.${h}`) : h)
      const row = Object.values(data)

      const content = [
        header,
        row
      ]
      exportAsCsv(content, `${fileName}.csv`)
    },
    downloadTable () {
      const header = [
        this.$t('campaignDesigner.finalCheckAndCampaignRelease.numberOfContacts'),
        this.$t('campaignDesigner.finalCheckAndCampaignRelease.numberOfContactsEmailGranted'),
        this.$t('campaignDesigner.finalCheckAndCampaignRelease.numberOfContactsSmsGranted'),
        this.$t('campaignDesigner.finalCheckAndCampaignRelease.numberOfCars'),
        this.$t('campaignDesigner.finalCheckAndCampaignRelease.numberOfDistinctEmail'),
        this.$t('campaignDesigner.finalCheckAndCampaignRelease.numberOfDistinctSMS')
      ]

      const row = Object.values(this.previewData.totalCount)

      const content = [ header, row ]

      exportAsCsv(content, 'Preview.csv')
    },
    getSectionToSave () {
      return {
        config: {
          jobId: this.jobId
        }
      }
    },
    async poll () {
      const response = await this.$store.dispatch('fastCounter/checkPreview', { jobId: this.jobId })
      if (response.status === 'Completed') {
        const preview = await this.$store.dispatch('fastCounter/poll', { previewId: response.detail.fastCounterId.toString() })
        this.previewData = preview.preview[0].preview
        this.computedPreview = true
      } else if (response.status === 'Error') {
        this.previewError = response.statusDetail
      }
    },
    async startComputePreview (previewId) {
      const startPreviewBody = {
        fastCounterId: this.previewId || previewId,
        department: 'FAST_COUNTER'
      }

      const response = await this.$store.dispatch('fastCounter/startPreview', startPreviewBody)
      this.jobId = response.id
    },
    async startPreviewPolling () {
      this.refreshJobInterval = setInterval(this.poll, 5000)
    },
    validate () {
      return true
    }
  },
  watch: {
    previewData (data) {
      if (data) {
        this.charts = [
          {
            translate: true,
            key: 'consumerTypeDistribution',
            title: 'Distribution by Consumer Type',
            type: 'PieChart',
            data: [
              ['Consumer Type', 'Count'],
              ['Customer', data.consumerTypeDistribution.customer ],
              ['Old Customer', data.consumerTypeDistribution.oldCustomer ],
              ['Prospect', data.consumerTypeDistribution.prospect ]
            ],
            options: {
              colors: [ '#00448B', '#005FAA', '#4485C6' ],
              pieSliceText: 'label',
              width: 600,
              height: 600,
              sliceVisibility: 0.0001
            }
          },
          {
            translate: true,
            key: 'privacyDistribution',
            title: 'Distribution by Email and SMS Privacy',
            type: 'ColumnChart',
            data: [
              ['Granted', 'G', 'F'],
              ['Privacy Email', data.privacyDistribution.privacyEmailG, data.privacyDistribution.privacyEmailF],
              ['Privacy Sms', data.privacyDistribution.privacySmsG, data.privacyDistribution.privacySmsF]
            ],
            options: {
              colors: ['#00448B', '#005FAA', '#4485C6', '#7AB0E0', '#98CAFF', '#D6EAFF'],
              legend: { position: 'right', maxLines: 3 },
              bar: { groupWidth: '75%' },
              isStacked: true,
              width:  600,
              height: 600,
            }
          }
        ]

        if (data.brandDistribution) {
          this.charts.push(
            {
            translate: true,
            key: 'carBrandDistribution',
            title: 'Distribution by Brand',
            type: 'BarChart',
            data: [
              [ this.$t('fastCounter.finalCheck.brand'), this.$t('fastCounter.finalCheck.count') ],
              ...[
                [ this.$t('fastCounter.finalCheck.abarth'), data.brandDistribution.abarth ],
                [ this.$t('fastCounter.finalCheck.alfa'), data.brandDistribution.alfa ],
                [ this.$t('fastCounter.finalCheck.fiat'), data.brandDistribution.fiat ],
                [ this.$t('fastCounter.finalCheck.fiatPro'), data.brandDistribution.fiatPro ],
                [ this.$t('fastCounter.finalCheck.jeep'), data.brandDistribution.jeep ],
                [ this.$t('fastCounter.finalCheck.lancia'), data.brandDistribution.lancia ]
              ]
            ],
            options: {
              colors: ['#00448B', '#005FAA', '#4485C6', '#7AB0E0', '#98CAFF', '#D6EAFF'],
              width: 600,
              height: 600
            }
          }
          )
        }

        if (data.carBrandDistribution) {
          this.charts.push({
            translate: true,
            key: 'carBrandDistribution',
            title: 'Distribution by Brand (Car)',
            type: 'BarChart',
            data: [
              [ this.$t('fastCounter.finalCheck.brand'), this.$t('fastCounter.finalCheck.count') ],
              ...[
                [ this.$t('fastCounter.finalCheck.abarth'), data.carBrandDistribution.abarth ],
                [ this.$t('fastCounter.finalCheck.alfa'), data.carBrandDistribution.alfa ],
                [ this.$t('fastCounter.finalCheck.fiat'), data.carBrandDistribution.fiat ],
                [ this.$t('fastCounter.finalCheck.fiatPro'), data.carBrandDistribution.fiatPro ],
                [ this.$t('fastCounter.finalCheck.jeep'), data.carBrandDistribution.jeep ],
                [ this.$t('fastCounter.finalCheck.lancia'), data.carBrandDistribution.lancia ]
              ]
            ],
            options: {
              colors: ['#00448B', '#005FAA', '#4485C6', '#7AB0E0', '#98CAFF', '#D6EAFF'],
              width: 600,
              height: 600
            }
          },)
        }

        if (data.leadBrandDistribution) {
          this.charts.push({
            translate: true,
            key: 'leadBrandDistribution',
            title: 'Distribution by Brand (Lead)',
            type: 'BarChart',
            data: [
              [ this.$t('fastCounter.finalCheck.brand'), this.$t('fastCounter.finalCheck.count') ],
              ...[
                [ this.$t('fastCounter.finalCheck.abarth'), data.leadBrandDistribution.abarth ],
                [ this.$t('fastCounter.finalCheck.alfa'), data.leadBrandDistribution.alfa ],
                [ this.$t('fastCounter.finalCheck.fiat'), data.leadBrandDistribution.fiat ],
                [ this.$t('fastCounter.finalCheck.fiatPro'), data.leadBrandDistribution.fiatPro ],
                [ this.$t('fastCounter.finalCheck.jeep'), data.leadBrandDistribution.jeep ],
                [ this.$t('fastCounter.finalCheck.lancia'), data.leadBrandDistribution.lancia ]
              ]
            ],
            options: {
              colors: ['#00448B', '#005FAA', '#4485C6', '#7AB0E0', '#98CAFF', '#D6EAFF'],
              width: 600,
              height: 600
            }
          })
        }

        if (data.permBrandDistribution) {
          this.charts.push({
            translate: true,
            key: 'permBrandDistribution',
            title: 'Distribution by Brand (Permission)',
            type: 'BarChart',
            data: [
              [ this.$t('fastCounter.finalCheck.brand'), this.$t('fastCounter.finalCheck.count') ],
              ...[
                [ this.$t('fastCounter.finalCheck.abarth'), data.permBrandDistribution.abarth ],
                [ this.$t('fastCounter.finalCheck.alfa'), data.permBrandDistribution.alfa ],
                [ this.$t('fastCounter.finalCheck.fiat'), data.permBrandDistribution.fiat ],
                [ this.$t('fastCounter.finalCheck.fiatPro'), data.permBrandDistribution.fiatPro ],
                [ this.$t('fastCounter.finalCheck.jeep'), data.permBrandDistribution.jeep ],
                [ this.$t('fastCounter.finalCheck.lancia'), data.permBrandDistribution.lancia ]
              ]
            ],
            options: {
              colors: ['#00448B', '#005FAA', '#4485C6', '#7AB0E0', '#98CAFF', '#D6EAFF'],
              width: 600,
              height: 600
            }
          })
        }

        if (data.quoteCarBrandDistribution) {
          this.charts.push({
            translate: true,
            key: 'quoteCarBrandDistribution',
            title: 'Distribution by Brand (Quote)',
            type: 'BarChart',
            data: [
              [ this.$t('fastCounter.finalCheck.brand'), this.$t('fastCounter.finalCheck.count') ],
              ...[
                [ this.$t('fastCounter.finalCheck.abarth'), data.quoteCarBrandDistribution.abarth ],
                [ this.$t('fastCounter.finalCheck.alfa'), data.quoteCarBrandDistribution.alfa ],
                [ this.$t('fastCounter.finalCheck.fiat'), data.quoteCarBrandDistribution.fiat ],
                [ this.$t('fastCounter.finalCheck.fiatPro'), data.quoteCarBrandDistribution.fiatPro ],
                [ this.$t('fastCounter.finalCheck.jeep'), data.quoteCarBrandDistribution.jeep ],
                [ this.$t('fastCounter.finalCheck.lancia'), data.quoteCarBrandDistribution.lancia ]
              ]
            ],
            options: {
              colors: ['#00448B', '#005FAA', '#4485C6', '#7AB0E0', '#98CAFF', '#D6EAFF'],
              width: 600,
              height: 600
            }
          })
        }

        this.charts.push({
          key: 'yearDistribution',
          title: 'Distribution by Reg. Year',
          type: 'ColumnChart',
          data: [
            [ this.$t('fastCounter.finalCheck.year'), this.$t('fastCounter.finalCheck.count') ],
            ...(Object.entries(data.yearDistribution)
              .filter(e => e[1] > 0)
              .sort((a, b) => (a[0].replace('<', '-')) - b[0].replace('<', '-')))
          ],
          options: {
            colors: ['#00448B', '#005FAA', '#4485C6', '#7AB0E0', '#98CAFF', '#D6EAFF'],
            width: 600,
            height: 600
          }
        })
      }
    },
    status (newValue) {
      if (newValue === 'COMPUTING') {
        this.startPreviewPolling()
      } else if (newValue === 'COMPUTED') {
        if (this.refreshJobInterval) {
          clearInterval(this.refreshJobInterval)
        }
      }
    }
  },
  beforeDestroy () {
    if (this.refreshJobInterval) {
      clearInterval(this.refreshJobInterval)
    }
  }
}
</script>
<style lang="scss" scoped>
  .chart-container {
    margin-bottom: 10px;
  }
  .container {
    display: flex;
    justify-content: center;
  }
  .spinner {
    margin-top: 20px;
  }

  .slds-card + .slds-card {
    margin-top: 0rem !important;
  }
</style>
